import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { Check, ShareNetwork } from "@phosphor-icons/react";
import { brandify } from "../components/brand";

const SHARE_TEXT =
  "Am intrat pe suuper — cu cât suntem mai mulți, cu atât plătim mai puțin.";

type FondatorSearch = {
  cod?: string;
};

export const Route = createFileRoute("/fondator")({
  validateSearch: (search: Record<string, unknown>): FondatorSearch => ({
    cod:
      typeof search.cod === "string"
        ? search.cod.trim().toUpperCase().slice(0, 12)
        : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Progresul tău de fondator — suuper" },
      // Pagina e personală (cheia e codul din email), n-are ce căuta în index.
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: FondatorPage,
});

/**
 * Pagina de progres a unui fondator. Linkul vine din emailul de confirmare și
 * de pe ecranul de succes; cheia e codul de referral — un secret „la purtător".
 * Nu afișează nimic personal: doar numărul de fondator și câți prieteni au
 * intrat cu linkul lui.
 */
function FondatorPage() {
  const { cod } = Route.useSearch();
  const status = useQuery(
    api.waitlist.getFounderStatus,
    cod ? { referralCode: cod } : "skip"
  );
  const [copied, setCopied] = useState(false);

  const shareUrl = cod ? `${window.location.origin}/join?ref=${cod}` : "";

  const handleShare = async () => {
    const text = `${SHARE_TEXT} ${shareUrl}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "suuper", text });
        return;
      } catch {
        // anulat — cădem pe copiere
      }
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // selectează manual linkul afișat
    }
  };

  return (
    <div className="join-page relative flex min-h-screen flex-col bg-white">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-[420px] bg-gradient-to-b from-primary/8 via-primary/4 to-transparent"
      />

      <header className="relative w-full">
        <nav
          className="container mx-auto flex max-w-2xl items-center justify-between gap-3 px-4 py-4"
          aria-label="Navigație principală"
        >
          <Link to="/join">
            <img
              src="/suuper_logo.svg"
              alt="suuper"
              className="h-6 w-auto sm:h-7"
            />
          </Link>
        </nav>
      </header>

      <main className="relative flex-1 px-4 pb-20 pt-10 sm:pt-16">
        <div className="container mx-auto max-w-md text-center">
          {!cod || status === null ? (
            <>
              <h1 className="text-3xl font-bold tracking-tight text-text">
                Nu găsim acest cod.
              </h1>
              <p className="mt-4 text-text-light leading-relaxed">
                Linkul corect e în emailul de confirmare primit la înscriere.
                Dacă nu ești încă în club, locul tău te așteaptă.
              </p>
              <Link
                to="/join"
                className="join-cta mt-8 inline-flex items-center justify-center rounded-2xl bg-primary-gradient px-8 py-4 text-lg font-semibold text-white shadow-lg shadow-primary/25"
              >
                Intră în suuper club
              </Link>
            </>
          ) : status === undefined ? (
            // Query în zbor: schelet minim, fără spinner care să domine pagina
            <p className="text-text-muted">Se încarcă…</p>
          ) : (
            <>
              {status.founderNumber !== null && (
                <p className="text-[12px] font-bold tracking-[0.18em] text-primary">
                  FONDATOR #{status.founderNumber}
                </p>
              )}

              <h1 className="mt-3 text-3xl font-bold tracking-tight text-text sm:text-4xl">
                {brandify(
                  status.referrals >= status.milestone
                    ? "Ești în primul val de acces."
                    : "Drumul tău spre primul val."
                )}
              </h1>

              {/* Progresul: puncte mari, citibile dintr-o privire */}
              <div
                className="mt-8 flex items-center justify-center gap-3"
                role="img"
                aria-label={`${Math.min(status.referrals, status.milestone)} din ${status.milestone} prieteni`}
              >
                {Array.from({ length: status.milestone }, (_, i) => (
                  <span
                    key={i}
                    className={`flex h-14 w-14 items-center justify-center rounded-2xl text-lg font-bold ${
                      i < status.referrals
                        ? "bg-primary text-white"
                        : "border-2 border-dashed border-border-dark text-text-muted"
                    }`}
                  >
                    {i < status.referrals ? (
                      <Check size={24} weight="bold" />
                    ) : (
                      i + 1
                    )}
                  </span>
                ))}
              </div>

              <p className="mt-6 text-text-light leading-relaxed">
                {status.referrals >= status.milestone ? (
                  <>
                    <strong className="text-text">
                      {status.referrals}{" "}
                      {status.referrals === 1 ? "prieten a intrat" : "prieteni au intrat"}
                    </strong>{" "}
                    cu linkul tău. Primul val de acces e al tău — și fiecare om
                    în plus ne dă mai multă putere la negociere.
                  </>
                ) : (
                  <>
                    <strong className="text-text">
                      {status.referrals} din {status.milestone}
                    </strong>{" "}
                    — adu {status.milestone - status.referrals}{" "}
                    {status.milestone - status.referrals === 1
                      ? "prieten"
                      : "prieteni"}{" "}
                    și urci în primul val de acces. Nu câștigi bani din invitați
                    — urcăm împreună.
                  </>
                )}
              </p>

              <button
                type="button"
                onClick={handleShare}
                className="join-cta mt-8 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-primary-gradient py-4 text-lg font-semibold text-white shadow-lg shadow-primary/25"
              >
                {copied ? (
                  <>
                    <Check size={20} weight="bold" />
                    Link copiat
                  </>
                ) : (
                  <>
                    <ShareNetwork size={20} weight="bold" />
                    Invită un prieten
                  </>
                )}
              </button>

              <p className="mt-4 break-all text-sm text-text-muted">
                {shareUrl}
              </p>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
